import { Injectable, ComponentRef, ApplicationRef, Injector, EmbeddedViewRef, ComponentFactoryResolver, OnDestroy } from '@angular/core';
import { SwUpdate, VersionEvent, VersionReadyEvent } from '@angular/service-worker';
import { AppUpdateDialogComponent } from './app-update-dialog/app-update-dialog.component';
import { take, filter } from 'rxjs/operators';

@Injectable({
    providedIn: 'root'
})
export class AppUpdateService implements OnDestroy {
    private dialogComponentRef: ComponentRef<AppUpdateDialogComponent> | null = null;
    private minutesToUpdateCheck: number = 2;
    private updateInterval: any;
    private hasDismissedUpdate: boolean = false;
    private isCheckingForUpdate: boolean = false;

    constructor(
        private swUpdate: SwUpdate,
        private componentFactoryResolver: ComponentFactoryResolver,
        private appRef: ApplicationRef,
        private injector: Injector
    ) { }

    /**
     * Inicia a verificação de atualizações da aplicação
     */
    checkForUpdates(): void {
        if (!this.swUpdate.isEnabled) {
            console.warn('⚠️ Service Worker não está habilitado. Verifique se está em modo produção.');
            return;
        }

        console.log('✅ Service Worker habilitado, iniciando verificação de atualizações');
        console.log('📋 Configuração:', {
            enabled: this.swUpdate.isEnabled,
            minutesToCheck: this.minutesToUpdateCheck
        });

        // Detecta quando uma nova versão está disponível
        this.swUpdate.versionUpdates.subscribe(event => {
            console.log('📦 Evento de versão recebido:', event);
            
            if (event.type === 'VERSION_READY') {
                console.log('🎉 Nova versão pronta para instalação!', {
                    currentVersion: event.currentVersion,
                    latestVersion: event.latestVersion
                });
                this.promptUser();
            } else if (event.type === 'VERSION_DETECTED') {
                console.log('🔍 Nova versão detectada, fazendo download...', event);
            } else if (event.type === 'VERSION_INSTALLATION_FAILED') {
                console.error('❌ Falha na instalação da nova versão:', event);
                console.error('💡 Detalhes do erro:', {
                    version: event.version,
                    error: event.error
                });
                
                // Hash mismatch geralmente indica problemas de cache
                if (event.error && event.error.includes('Hash mismatch')) {
                    console.warn('🔧 Detectado Hash mismatch - tentando limpar cache e recarregar...');
                    this.handleHashMismatch();
                }
            } else if (event.type === 'NO_NEW_VERSION_DETECTED') {
                console.log('✓ Nenhuma nova versão disponível');
            }
        });

        // Detecta erros não tratados do Service Worker
        this.swUpdate.unrecoverable.subscribe(event => {
            console.error('💥 Estado irrecuperável do Service Worker:', event);
            alert('Ocorreu um erro irrecuperável. A página será recarregada.');
            document.location.reload();
        });

        // Verifica periodicamente por atualizações (a cada 2 minutos)
        this.scheduleNextUpdateCheck(this.minutesToUpdateCheck);

        // Primeira verificação após 5 segundos (aguarda estabilização do app)
        console.log('⏳ Primeira verificação agendada para daqui a 5 segundos...');
        setTimeout(() => {
            this.performUpdateCheck();
        }, 5000);
    }

    /**
     * Realiza a verificação de atualização com controle de concorrência
     */
    private performUpdateCheck(): void {
        if (this.isCheckingForUpdate) {
            console.log('⏭️ Verificação de atualização já em andamento, pulando...');
            return;
        }

        this.isCheckingForUpdate = true;
        console.log('🔄 Verificando por atualizações no servidor...');

        this.swUpdate.checkForUpdate().then((updateFound) => {
            if (updateFound) {
                console.log('🎯 Atualização encontrada! Aguardando evento VERSION_READY...');
            } else {
                console.log('✓ Nenhuma atualização disponível no momento');
            }
            this.isCheckingForUpdate = false;
        }).catch(err => {
            console.error('❌ Erro ao verificar atualização:', err);
            this.isCheckingForUpdate = false;
        });
    }

    /**
     * Lida com erro de Hash mismatch - limpa Service Worker e recarrega
     */
    private async handleHashMismatch(): Promise<void> {
        console.log('🧹 Iniciando limpeza de cache devido a Hash mismatch...');
        return;
        
        try {
            // Desregistra todos os Service Workers
            if ('serviceWorker' in navigator) {
                const registrations = await navigator.serviceWorker.getRegistrations();
                console.log(`🗑️ Encontrados ${registrations.length} Service Worker(s) registrado(s)`);
                
                for (const registration of registrations) {
                    await registration.unregister();
                    console.log('✅ Service Worker desregistrado');
                }
            }

            // Limpa todos os caches
            if ('caches' in window) {
                const cacheNames = await caches.keys();
                console.log(`🗑️ Encontrados ${cacheNames.length} cache(s)`);
                
                for (const cacheName of cacheNames) {
                    await caches.delete(cacheName);
                    console.log(`✅ Cache "${cacheName}" removido`);
                }
            }

            console.log('🔄 Cache limpo com sucesso, recarregando página em 2 segundos...');
            
            // Aguarda 2 segundos antes de recarregar para garantir limpeza
            setTimeout(() => {
                // Força reload completo sem cache (Ctrl+Shift+R programático)
                window.location.reload();
            }, 2000);
            
        } catch (error) {
            console.error('❌ Erro ao limpar cache:', error);
            console.log('🔄 Tentando recarregar mesmo assim...');
            window.location.reload();
        }
    }

    /**
     * Exibe uma confirmação ao usuário para atualizar a aplicação
     */
    private promptUser(): void {
        if (this.dialogComponentRef) {
            console.log('⚠️ Diálogo de atualização já está aberto');
            return;
        }

        console.log('🔔 Exibindo diálogo de atualização para o usuário');

        try {
            // Cria o componente dinamicamente
            const componentFactory = this.componentFactoryResolver.resolveComponentFactory(AppUpdateDialogComponent);
            this.dialogComponentRef = componentFactory.create(this.injector);

            // Anexa o componente à view
            this.appRef.attachView(this.dialogComponentRef.hostView);

            // Obtém o elemento DOM do componente
            const domElem = (this.dialogComponentRef.hostView as EmbeddedViewRef<any>).rootNodes[0] as HTMLElement;

            // Adiciona ao body
            document.body.appendChild(domElem);

            // Abre o diálogo
            this.dialogComponentRef.instance.openDialog();

            // Escuta a resposta do usuário (apenas uma vez para evitar memory leak)
            this.dialogComponentRef.instance.confirmUpdate.pipe(take(1)).subscribe((shouldUpdate: boolean) => {
                console.log('👤 Resposta do usuário:', shouldUpdate ? '✅ Atualizar' : '⏭️ Não agora');
                
                if (shouldUpdate) {
                    this.activateUpdate();
                    return;
                }

                // Usuário optou por não atualizar agora
                this.hasDismissedUpdate = true;

                this.minutesToUpdateCheck += 2;

                if (this.minutesToUpdateCheck > 10) {
                    this.minutesToUpdateCheck = 10;
                }

                console.log(`⏰ Próxima verificação de atualização em ${this.minutesToUpdateCheck} minuto(s)`);
                this.scheduleNextUpdateCheck(this.minutesToUpdateCheck);

                // Remove o componente após uso
                this.destroyDialog();
            });
        } catch (error) {
            console.error('❌ Erro ao criar diálogo de atualização:', error);
        }
    }

    /**
     * Remove o componente do dialog da DOM
     */
    private destroyDialog(): void {
        if (this.dialogComponentRef) {
            this.appRef.detachView(this.dialogComponentRef.hostView);
            this.dialogComponentRef.destroy();
            this.dialogComponentRef = null;
        }
    }

    /**
     * Ativa a atualização e recarrega a página
     */
    private activateUpdate(): void {
        console.log('🔄 Ativando atualização...');
        
        // Destruir dialog antes de atualizar
        this.destroyDialog();
        
        this.swUpdate.activateUpdate().then(() => {
            console.log('✅ Atualização ativada, recarregando página...');
            document.location.reload();
        }).catch(err => {
            console.error('❌ Erro ao ativar atualização:', err);
            // Tenta recarregar mesmo assim
            document.location.reload();
        });
    }

    /**
     * Agenda intervalo de verificação de atualização
     * @param minutes 
     */
    scheduleNextUpdateCheck(minutes: number): void {
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
        }

        console.log(`⏰ Agendando próxima verificação de atualização em ${minutes} minuto(s)`);

        this.updateInterval = setInterval(() => {
            console.log('⏰ Executando verificação periódica agendada');
            
            if (this.hasDismissedUpdate) {
                console.log('📝 Usuário dispensou a atualização anteriormente, verificando novamente...');
                this.hasDismissedUpdate = false; // Reseta flag para permitir nova verificação
            }
            
            this.performUpdateCheck();
        }, minutes * 60 * 1000);
    }

    /**
     * Limpa recursos ao destruir o serviço
     */
    ngOnDestroy(): void {
        if (this.updateInterval) {
            clearInterval(this.updateInterval);
        }
        this.destroyDialog();
    }
}
